import React, { useCallback, useMemo, useRef, useState } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getUsers, saveBatchUsers, unlockUser } from '../../api/users';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit as canEditUtil } from '../../utils/permissions';
import { LopButton } from '../../components/LopButton/LopButton';
import { showErrorToast } from '../../components/ErrorToast';
import { UnlockUserDialog } from './UnlockUserDialog';
import { parseUtcTimestamp } from '../../utils/dateTime';
import { useTenants } from '../../hooks/useTenants';
import type { UserRow } from '../../types/User';
import { PasswordCellEditor } from '../../components/cells/PasswordCellEditor';
import { RolesCellEditor } from '../../components/cells/RolesCellEditor';
import { RolesCellRenderer } from '../../components/cells/RolesCellRenderer';
import { t } from '../../i18n/t';
import '../../components/cells/cells.css';
import { formatDateTimeIL } from '../../utils/dateTime';

type RowStatusData = { __status?: string } | undefined;
const isAddedRow = (data: RowStatusData) => data?.__status === 'added';

function formatDate(value: unknown): string {
  return formatDateTimeIL(value);
}

function formatDateTime(value: unknown): string {
  return formatDateTimeIL(value);
}

const newUserDefaults = (): Partial<UserRow> => ({
  userId: 0,
  email: '',
  password: '',
  displayName: '',
  tenantId: null,
  isActive: true,
  roleIds: [],
  createdAt: new Date().toISOString(),
  lastLoginAt: null,
});

export function UsersPage() {
  const { tenants, loading, error } = useTenants();
  const { user: me } = useAuth();
  const canEdit = canEditUtil(me, 'users');
  const [unlockTarget, setUnlockTarget] = useState<UserRow | null>(null);
  // userIds unlocked in this session: hides the lock badge at once, before the next refetch confirms it.
  const unlockedRef = useRef<Set<number>>(new Set());
  const gridApiForRefresh = useRef<ICellRendererParams<UserRow> | null>(null);
  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;

  const requestUnlock = useCallback((row: UserRow, params: ICellRendererParams<UserRow>) => {
    gridApiForRefresh.current = params;
    setUnlockTarget(row);
  }, []);

  const confirmUnlock = useCallback(async () => {
    const target = unlockTarget;
    if (!target) return;
    try {
      await unlockUser(target.userId);
      unlockedRef.current.add(target.userId);
      const p = gridApiForRefresh.current;
      if (p?.node) p.api.refreshCells({ rowNodes: [p.node], force: true });
    } catch (err) {
      showErrorToast(err, {});
    } finally {
      setUnlockTarget(null);
    }
  }, [unlockTarget]);

  const columnDefs = useMemo<ColDef<UserRow>[]>(() => {
    if (!tenants) return [];

    const allTenantsLabel = t('users.allTenants');
    const active = tenants.filter((tn) => tn.isActive);
    const sorted = [...active].sort((a, b) =>
      a.displayName.localeCompare(b.displayName, 'he'),
    );
    const tenantNames = sorted.map((tn) => tn.displayName);
    const nameById = new Map<number, string>(
      tenants.map((tn) => [tn.tenantId, tn.displayName]),
    );
    const idByName = new Map<string, number>(
      tenants.map((tn) => [tn.displayName, tn.tenantId]),
    );

    return [
      {
        field: 'userId',
        headerName: t('users.columns.userId'),
        width: 90,
        editable: false,
        valueFormatter: (p: ValueFormatterParams<UserRow>) =>
          isAddedRow(p.data as RowStatusData) || p.value == null
            ? ''
            : String(p.value),
      },
      {
        field: 'email',
        headerName: t('users.columns.email'),
        flex: 1,
        // Frozen after creation — natural identity field.
        editable: (params: EditableCallbackParams<UserRow>) =>
          isAddedRow(params.data as RowStatusData),
      },
      {
        field: 'password',
        headerName: t('users.columns.password'),
        width: 140,
        editable: true,
        cellEditor: PasswordCellEditor,
        headerTooltip: t('users.passwordPolicyHint'),
        // Never render the typed value in plaintext; mask any transient value.
        valueFormatter: (p: ValueFormatterParams<UserRow>) =>
          p.value ? '••••••••' : '',
      },
      {
        field: 'displayName',
        headerName: t('users.columns.displayName'),
        flex: 1,
        editable: true,
      },
      {
        field: 'tenantId',
        headerName: t('users.columns.tenantId'),
        flex: 1,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: [allTenantsLabel, ...tenantNames] },
        valueGetter: (p: ValueGetterParams<UserRow>) => {
          if (!p.data) return allTenantsLabel;
          return p.data.tenantId == null
            ? allTenantsLabel
            : nameById.get(p.data.tenantId) ?? allTenantsLabel;
        },
        valueSetter: (p: ValueSetterParams<UserRow>) => {
          if (!p.data) return false;
          if (p.newValue === allTenantsLabel) {
            p.data.tenantId = null;
            return true;
          }
          const id = idByName.get(p.newValue);
          if (id === undefined) return false;
          p.data.tenantId = id;
          return true;
        },
      },
      {
        field: 'isActive',
        headerName: t('users.columns.isActive'),
        width: 90,
        editable: true,
        cellDataType: 'boolean',
      },
      {
        field: 'roleIds',
        headerName: t('users.columns.roleIds'),
        flex: 1,
        editable: true,
        cellDataType: false, // array value owned entirely by custom cells
        cellRenderer: RolesCellRenderer,
        cellEditor: RolesCellEditor,
        cellEditorPopup: true,
      },
      {
        colId: 'lockout',
        headerName: t('users.columns.lockout'),
        width: 230,
        editable: false,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<UserRow>) => {
          const row = params.data;
          const until = parseUtcTimestamp(row?.lockoutUntil);
          if (!row || !until || until.getTime() <= Date.now() || unlockedRef.current.has(row.userId)) {
            return null;
          }
          return (
            <span className="users-lockout-cell" data-testid="user-locked">
              <span>{t('users.lockout.lockedUntil', { time: formatDateTimeIL(row.lockoutUntil) })}</span>
              <LopButton
                size="sm"
                variant="secondary"
                disabled={!canEditRef.current}
                onClick={() => requestUnlock(row, params)}
              >
                {t('users.lockout.unlock')}
              </LopButton>
            </span>
          );
        },
      },
      {
        field: 'lastLoginAt',
        headerName: t('users.columns.lastLoginAt'),
        width: 160,
        editable: false,
        valueFormatter: (p: ValueFormatterParams<UserRow>) =>
          p.value ? formatDateTime(p.value) : t('users.neverLoggedIn'),
      },
      {
        field: 'createdAt',
        headerName: t('users.columns.createdAt'),
        width: 120,
        editable: false,
        valueFormatter: (p: ValueFormatterParams<UserRow>) =>
          formatDate(p.value),
      },
    ];
  }, [tenants, requestUnlock]);

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('users.errorPrefix', { message: error })}
        </div>
      </div>
    );
  }

  if (loading || !tenants) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>
        {t('common.loading')}
      </div>
    );
  }

  return (
    <>
    <LopCrudPage<UserRow>
      resourceCode="users"
      titleKey="users.pageTitle"
      idField="userId"
      getRows={getUsers}
      saveBatch={(added, modified, deletedIds) =>
        saveBatchUsers(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newUserDefaults}
    />
    {unlockTarget && (
      <UnlockUserDialog
        email={unlockTarget.email}
        onConfirm={confirmUnlock}
        onClose={() => setUnlockTarget(null)}
      />
    )}
    </>
  );
}
